import {
    Activity,
    AlertTriangle,
    Anchor,
    ArrowLeft,
    Armchair,
    Banknote,
    Battery,
    BatteryCharging,
    BatteryFull,
    Bell,
    BellOff,
    BellRing,
    Bluetooth,
    Bot,
    Bug,
    Building2,
    Calendar,
    CalendarClock,
    CalendarCheck,
    CalendarDays,
    Camera,
    Car,
    CarFront,
    Caravan,
    Cast,
    Cctv,
    CircleDot,
    CircleParking,
    CheckCircle2,
    ClipboardCheck,
    ClipboardList,
    Cog,
    Coins,
    Compass,
    Cpu,
    Crosshair,
    Disc3,
    DoorOpen,
    Download,
    Droplet,
    Eye,
    GitCommitVertical,
    History,
    Send,
    Play,
    Pause,
    FlaskConical,
    Copy,
    Factory,
    Fan,
    FileSpreadsheet,
    FileSignature,
    FileText,
    Flame,
    Fuel,
    Gauge,
    Gem,
    Globe,
    Hand,
    Handshake,
    Home,
    Hash,
    Headphones,
    Image,
    Inbox,
    Joystick,
    Key,
    KeyRound,
    Leaf,
    Lightbulb,
    Lock,
    LogOut,
    Luggage,
    Map as MapIcon,
    MapPin,
    ChevronDown,
    Maximize2,
    Megaphone,
    MessageSquareText,
    MessageSquareWarning,
    Mic,
    Monitor,
    Moon,
    Music,
    Navigation,
    Package,
    PackageCheck,
    Paintbrush,
    PaintBucket,
    Palette,
    Paperclip,
    Pencil,
    PencilLine,
    Plug,
    PlusCircle,
    Power,
    Radar,
    Radio,
    Receipt,
    RectangleHorizontal,
    Rocket,
    Route,
    Satellite,
    SatelliteDish,
    ScanLine,
    ScrollText,
    Shield,
    ShieldAlert,
    ShieldCheck,
    ShoppingBag,
    ShoppingCart,
    Siren,
    Smartphone,
    Snowflake,
    Sofa,
    Sparkles,
    Speaker,
    Star,
    Sun,
    Tablet,
    Tag,
    Thermometer,
    Trash2,
    TrendingUp,
    Truck,
    Van,
    Umbrella,
    Unlock,
    Upload,
    Unplug,
    Usb,
    User,
    Video,
    Warehouse,
    Waves,
    Wifi,
    Wind,
    Wrench,
    Zap,
    ZapOff,
    Box,
    FolderTree,
    Languages,
    RefreshCw,
    Replace,
    SlidersHorizontal,
    BookOpen,
    ExternalLink,
    ArrowUpDown,
    Check,
    Heart,
    LayoutGrid,
    List,
    Plus,
    Search,
    BanknoteArrowDown,
    BanknoteArrowUp,
    BarChart3,
    Boxes,
    Container,
    LayoutDashboard,
    ListChecks,
    PackageSearch,
    Settings,
    UserPlus,
    Reply,
    RotateCcw,
    XCircle,
    ArrowUpRight,
    ArrowDownRight,
    PiggyBank,
    FileImage,
    X,
    type LucideIcon,
    Save,
    Clock,
    Info,
    Layers,
    Percent,
    Settings2,
    StickyNote,
    Store,
    Landmark,
    CircleDashed,
    ArrowDownLeft,
    Undo2,
    Ban,
} from "lucide-react";

/**
 * Central icon registry.
 *
 * Single source of truth for icons that represent business entities,
 * their attributes, and (eventually) actions. Add new keys here rather
 * than importing lucide icons ad-hoc throughout the UI — that way every
 * surface that talks about a "vehicle" or a "claim" looks the same.
 *
 * Keys are canonical singular names. Aliases (e.g. plural forms used by
 * existing screens) are mapped via `ICON_ALIASES` below.
 */

export const MODEL_ICONS = {
    vehicle: Car,
    // A car advertised in a commercial channel (spec/listings.md).
    listing: Megaphone,
    opportunity: Handshake,
    order: ClipboardList,
    inventory: Warehouse,
    purchase: ShoppingBag,
    // The partner store's basket (spec/partner-portal-plan.md §8) — distinct
    // from `purchase`, which is our buying side.
    basket: ShoppingCart,
    // The partner portal's trading terms (plan §2.2).
    terms: ScrollText,
    invoice: Receipt,
    // The money side of the business — the Finance menu and both of its lists
    // (spec/finance-receivables-payables.md §5). Distinct from `invoice`, which
    // is the document.
    finance: Banknote,
    sales: TrendingUp,
    // What a car earned: the sale price less everything it cost us — the
    // profit panel of an account's trading overview.
    profit: PiggyBank,
    transport: Truck,
    // A parcel of a car's elements booked with a shipping integration.
    shipment: Package,
    load: Truck,
    inbox: Inbox,
    document: FileText,
    // A car's loose items — keys, papers, cables, tyres — filed in the Elements
    // Hotel (spec/elements-hotel.md). Deliberately not a key: a key is only one
    // kind of element, and the boxes read as "things kept on a shelf". Every
    // surface that talks about elements — the sidebar, the vehicle tab, the
    // hotel settings, an empty list — renders this one glyph.
    element: Boxes,
    account: Building2,
    contact: User,
    location: MapPin,
    claim: ShieldAlert,
    // Deliberately not a shield: the shield family is the claim, and an
    // anomaly is the opposite kind of thing — data that looks wrong, not
    // cover against a loss.
    anomaly: Bug,
    artifact: Paperclip,
    feedback: MessageSquareWarning,
    // A custom notification rule (spec/custom-notifications.md §11): the bell
    // in motion — something the tenant set up to ring, as opposed to the
    // fixed-topic `bell` attribute.
    "notification-rule": BellRing,
} as const satisfies Record<string, LucideIcon>;

export const ATTRIBUTE_ICONS = {
    damage: AlertTriangle,
    equipment: Wrench,
    manufacturer: Factory,
    model: Tag,
    mileage: Gauge,
    photo: Image,
    identifier: Hash,
    country: Globe,
    fuel: Fuel,
    transmission: Cog,
    color: Palette,
    date: Calendar,
    "factory-color": PaintBucket,
    comment: MessageSquareText,
    "model-year": CalendarDays,
    "registration-date": CalendarCheck,
    "license-plate": RectangleHorizontal,
    seats: Armchair,
    doors: DoorOpen,
    battery: BatteryFull,
    co2: Leaf,
    range: Route,
    "vehicle-type": CarFront,
    "body-type": Box,
    horsepower: Zap,
    "new-car": Sparkles,
    spreadsheet: FileSpreadsheet,
    // How a task came to exist: raised by the platform (workflow step,
    // integration callback) vs. typed in by a person.
    "task-auto": Bot,
    // The platform itself as the author of something — a workflow's own
    // notes in a comment thread, written by no person.
    system: Bot,
    "task-manual": Hand,
    // A workflow gate — a step that only holds others back: locked while its
    // condition is unmet, unlocked once released.
    "step-gate": Lock,
    "step-gate-released": Unlock,
    // A workflow read in the order its steps run, and what has happened on
    // it — the Sequence and History modes of a vehicle's workflow card.
    "workflow-sequence": GitCommitVertical,
    "workflow-history": History,
    // Account special settings (FDD §9 / §10.9.10) — the boolean flags that
    // gate buying- and sales-flow steps for a counterparty account.
    "transport-before-payment": Truck,
    "own-transport": Car,
    "vat-deposit": Coins,
    // What a VAT deposit supplier's package requires — the affidavits share
    // one icon, the rest reuse the icon of the document they ask for.
    "vat-deposit-affidavit": FileSignature,
    "vat-deposit-payment-confirmation": Banknote,
    "vat-deposit-transporter-invoice": Receipt,
    "compound-confirmation": CheckCircle2,
    "compound-announcement": Megaphone,
    release: Unlock,
    cmr: FileText,
    "entry-confirmation": ClipboardCheck,
    // Localization / language & region settings — the world, not a country.
    globe: Globe,
    // The other rows of an account's special settings (FDD §10.9.10): what a
    // value *is* — a cost, a percentage, a delay, a note, an integration to
    // book through, a default stock location, a payment trigger — and the
    // fallback glyph for a setting that has none of its own.
    cost: Coins,
    percentage: Percent,
    delay: Clock,
    note: StickyNote,
    integration: Plug,
    stock: Package,
    "payment-trigger": Banknote,
    "needs-confirmation": CheckCircle2,
    setting: Settings2,
    info: Info,
    // The counterparty roles an account's settings are grouped by.
    "role-buyer": ShoppingCart,
    "role-supplier": Truck,
    "role-vendor": Store,
    "role-transporter": Container,
    "role-compound": Layers,
    // The day a deal was struck — an account's latest deals.
    "deal-date": CalendarClock,
    // A figure up or down on the period it is compared against.
    increase: ArrowUpRight,
    decrease: ArrowDownRight,
} as const satisfies Record<string, LucideIcon>;

/**
 * Icons for the main application sidebar (`Layouts/AppLayout/AppNavigation`).
 *
 * Every destination in the sidebar has a glyph, sub-items included: a
 * collapsible group opens onto a list of children, and a child without an
 * icon reads as a second-class destination next to its siblings.
 *
 * Keys mirror the `navigation.*` translation keys, so a menu entry names its
 * icon with the same string it names its label with. Where a nav destination
 * is an existing business concept the glyph is re-used from the map above
 * rather than re-picked, so the sidebar agrees with the rest of the app.
 */
export const NAV_ICONS = {
    dashboard: LayoutDashboard,
    accounts: Building2,
    contacts: MODEL_ICONS.contact,
    leads: UserPlus,
    addresses: MapPin,
    vehicles: Car,
    sales: ShoppingCart,
    purchases: PackageSearch,
    logistics: Truck,
    logistics_load_handler: Container,
    logistics_check_in: ClipboardCheck,
    elements: MODEL_ICONS.element,
    // The list of every element, under the group that already carries the
    // element glyph.
    elements_index: List,
    elements_upload: Upload,
    elements_documents: FileText,
    elements_check_in: ClipboardCheck,
    elements_dispatch: PackageCheck,
    inbox: Inbox,
    tasks: ListChecks,
    claims: ShieldAlert,
    finance: Banknote,
    // The direction the money moves, off the same banknote as the parent:
    // receivables come in, payables go out.
    finance_receivables: BanknoteArrowDown,
    finance_payables: BanknoteArrowUp,
    // Payment runs: the batches paid to suppliers and their bank files.
    finance_payment_runs: Landmark,
    // VAT deposits: money held by suppliers, owed back to us.
    finance_vat_deposits: PiggyBank,
    reports: BarChart3,
    activity: ScrollText,
    settings: Settings,
} as const satisfies Record<string, LucideIcon>;

/**
 * Icons used by the Equipment dictionary (FDD 16). Each canonical
 * equipment root references one of these names via `icon_name`.
 */
export const EQUIPMENT_ICONS = {
    // Infotainment & connectivity
    speaker: Speaker,
    music: Music,
    radio: Radio,
    headphones: Headphones,
    monitor: Monitor,
    tablet: Tablet,
    smartphone: Smartphone,
    bluetooth: Bluetooth,
    wifi: Wifi,
    usb: Usb,
    cast: Cast,
    mic: Mic,
    joystick: Joystick,
    disc: Disc3,

    // Navigation & driver assistance
    navigation: Navigation,
    compass: Compass,
    map: MapIcon,
    radar: Radar,
    satellite: Satellite,
    "satellite-dish": SatelliteDish,
    camera: Camera,
    video: Video,
    cctv: Cctv,
    eye: Eye,
    scan: ScanLine,
    crosshair: Crosshair,
    parking: CircleParking,

    // Climate & comfort
    "climate-control": Thermometer,
    wind: Wind,
    fan: Fan,
    snowflake: Snowflake,
    sun: Sun,
    flame: Flame,
    "heated-seats": Waves,
    armchair: Armchair,
    sofa: Sofa,
    droplet: Droplet,
    umbrella: Umbrella,

    // Performance & powertrain
    gauge: Gauge,
    activity: Activity,
    rocket: Rocket,
    cog: Cog,
    cpu: Cpu,
    fuel: Fuel,
    plug: Plug,
    unplug: Unplug,
    "battery-charging": BatteryCharging,
    battery: Battery,
    "battery-full": BatteryFull,
    zap: Zap,
    "zap-off": ZapOff,
    power: Power,
    wheel: CircleDot,

    // Safety & security
    shield: Shield,
    "shield-check": ShieldCheck,
    lock: Lock,
    key: Key,
    "key-round": KeyRound,
    bell: Bell,
    siren: Siren,

    // Exterior, styling & lighting
    lightbulb: Lightbulb,
    "door-open": DoorOpen,
    paintbrush: Paintbrush,
    star: Star,
    gem: Gem,
    sparkles: Sparkles,
    moon: Moon,

    // Utility & towing
    truck: Truck,
    caravan: Caravan,
    luggage: Luggage,
    package: Package,
    anchor: Anchor,
    wrench: Wrench,
} as const satisfies Record<string, LucideIcon>;

/**
 * Icons used in the Equipment settings navigation tabs.
 */
export const EQUIPMENT_NAV_ICONS = {
    categories: FolderTree,
    nodes: Box,
    aliases: Replace,
    packages: Package,
    properties: SlidersHorizontal,
    translations: Languages,
    ai: Sparkles,
} as const satisfies Record<string, LucideIcon>;

/**
 * Action icons (edit, delete, view, …) belong here as we centralise more
 * of the UI. Populate as needs emerge so we don't accumulate an unused
 * catalogue.
 */
export const ACTION_ICONS = {
    create: PlusCircle,
    import: Download,
    // Look at a stored file (a shipping label, a packing slip) before saving it.
    preview: Eye,
    // A list's *Export* button and the Data exports page (FTR 412).
    export: FileSpreadsheet,
    upload: Upload,
    refresh: RefreshCw,
    warning: AlertTriangle,
    edit: Pencil,
    delete: Trash2,
    duplicate: Copy,
    simulate: FlaskConical,
    activate: Play,
    pause: Pause,
    send: Send,
    save: Save,
    back: ArrowLeft,
    home: Home,
    "sign-out": LogOut,
    // Partner portal orders (spec/partner-portal-plan.md §9): the transport
    // block, document rows and element icons.
    "external-link": ExternalLink,
    // Open a gallery or a panel full screen (the supplier portal's car photos).
    expand: Maximize2,
    // Open a collapsed row in place (the supplier portal's order list).
    disclosure: ChevronDown,
    "file-text": FileText,
    "check-circle": CheckCircle2,
    book: BookOpen,
    // Partner store (spec/partner-portal-plan.md §2.2 Cars): the heart, the
    // view toggle, the filter chips and the mobile sort / filter sheets.
    heart: Heart,
    bolt: Zap,
    grid: LayoutGrid,
    list: List,
    spark: Sparkles,
    filter: SlidersHorizontal,
    sort: ArrowUpDown,
    check: Check,
    plus: Plus,
    search: Search,
    // The seller's moves on a portal negotiation (spec/partner-portal-plan.md
    // §7, §16.4): counter, accept, decline, and a personal *target price*.
    counter: Reply,
    accept: Check,
    decline: XCircle,
    "target-price": Tag,
    // Triage of a vulnerability finding on Settings → Operations → Patching:
    // mute it, watch it, or send it back to open.
    mute: BellOff,
    watch: Eye,
    reopen: RotateCcw,
    // An uploaded document's row on Elements → Upload: what the file is, its
    // pipeline activity, where it went, and a run that failed.
    "file-image": FileImage,
    "activity-log": Activity,
    open: ArrowUpRight,
    failed: X,
    // Taking one value off a list (a document field's extra value).
    remove: X,
} as const satisfies Record<string, LucideIcon>;

/**
 * A load's lifecycle state as one glyph, for where a pill has no room — the
 * collapsed load row. Keyed `load-<LoadState value>` so a state resolves
 * straight from the value the backend sends; the `satisfies` clause makes a
 * new state in the enum a type error here until it gets a glyph.
 */
export const LOAD_STATE_ICONS = {
    "load-draft": PencilLine,
    "load-review": Eye,
    "load-sent_to_transporter": Send,
    "load-transporter_confirmed": CheckCircle2,
    "load-announced": Megaphone,
    "load-in_transit": Truck,
    // Still rolling, but only with the cars that were never ours to receive.
    "load-in_transit_direct": Route,
    "load-check_in": ClipboardCheck,
    "load-cmr_only": FileText,
    "load-issue": AlertTriangle,
    "load-complete": PackageCheck,
} as const satisfies Record<`load-${App.Enums.Logistics.LoadState}`, LucideIcon>;

/**
 * A vehicle's commercial class as one glyph. Keyed `vehicle-type-<VehicleType
 * value>` so a class resolves straight from the value the backend sends (and
 * from `VehicleType::icon()` on the PHP side); the `satisfies` clause makes a
 * new case in the enum a type error here until it gets a glyph.
 *
 * The silhouettes climb in size across the five classes — car, taller car, van,
 * high-roof box, cab-and-box — so the shape alone carries the ordering.
 */
export const VEHICLE_TYPE_ICONS = {
    "vehicle-type-passenger_car": CarFront,
    "vehicle-type-mpv_suv": Car,
    "vehicle-type-van": Van,
    "vehicle-type-large_van": Caravan,
    "vehicle-type-small_truck": Truck,
} as const satisfies Record<`vehicle-type-${App.Enums.Vehicle.VehicleType}`, LucideIcon>;

/**
 * What an order line sells, as one glyph. Keyed `product-<ProductType value>`
 * so a line resolves its icon straight from the value the backend sends, and
 * the `satisfies` clause makes a new case in the enum a type error here until
 * it gets a glyph.
 *
 * A vehicle line shows the car's photo rather than a glyph (`VehicleThumb`);
 * these are what every OTHER kind of line shows in that same slot, so a
 * transport leg is recognisable at a glance instead of reading as an untitled
 * text row.
 */
export const PRODUCT_TYPE_ICONS = {
    "product-vehicle": Car,
    "product-transport": Truck,
    "product-storage": Warehouse,
    "product-inspection": ClipboardCheck,
    "product-other": Package,
} as const satisfies Record<`product-${App.Enums.Orders.ProductType}`, LucideIcon>;

/**
 * Money labels as one glyph each: a payment status (the `LinePaymentStatus`
 * cases, spec/erp-transactions-and-payments.md §11.2) and an invoice's kind —
 * money in for what we billed, money out for what the supplier billed us.
 */
export const FINANCE_ICONS = {
    "payment-not-invoiced": CircleDashed,
    "payment-invoiced": FileText,
    "payment-partially-paid": CircleDot,
    "payment-paid": Check,
    "payment-overpaid": AlertTriangle,
    "payment-review": AlertTriangle,
    "invoice-sale": ArrowDownLeft,
    "invoice-supplier": ArrowUpRight,
    "invoice-credit-note": Undo2,
    "invoice-cancelled": Ban,
    // A car's P&L tab (spec/finance-costs.md §10).
    "profit-and-loss": Coins,
} as const satisfies Record<string, LucideIcon>;

export const ICON_REGISTRY = {
    ...MODEL_ICONS,
    ...ATTRIBUTE_ICONS,
    ...EQUIPMENT_ICONS,
    ...ACTION_ICONS,
    ...LOAD_STATE_ICONS,
    ...VEHICLE_TYPE_ICONS,
    ...PRODUCT_TYPE_ICONS,
    ...FINANCE_ICONS,
} as const;

export type IconName = keyof typeof ICON_REGISTRY;

/**
 * Plural / synonymous spellings that should resolve to a canonical key.
 * Add sparingly — only when an existing screen reasonably uses the alias.
 */
const ICON_ALIASES: Record<string, IconName> = {
    vehicles: "vehicle",
    opportunities: "opportunity",
    orders: "order",
    purchases: "purchase",
    download: "import",
    documents: "document",
    elements: "element",
    accounts: "account",
    contacts: "contact",
    address: "location",
    addresses: "location",
    locations: "location",
    "map-pin": "location",
    map_pin: "location",
    pickup_location: "location",
    delivery_location: "location",
    claims: "claim",
    artifacts: "artifact",
    photos: "photo",
    images: "photo",
    damages: "damage",
    manufacturers: "manufacturer",
    models: "model",
    vin: "identifier",
    license_plate: "license-plate",
    factory_color: "factory-color",
    model_year: "model-year",
    registration_date: "registration-date",
    first_registration_date: "registration-date",
    co2_wltp: "co2",
    battery_size: "battery",
    mileage_km: "mileage",
    fuel_type: "fuel",
    vehicle_type: "vehicle-type",
    body_type: "body-type",
    is_new_car: "new-car",
    vat_status: "percentage",
    // AccountSpecialSetting enum values, so a flag resolves straight from the
    // value the backend sends without the caller re-spelling it.
    transport_before_payment: "transport-before-payment",
    transport_before_payment_in: "transport-before-payment",
    vat_deposit_required: "vat-deposit",
    vat_deposit_requires_purchase_invoice: "invoice",
    vat_deposit_requires_vat_invoice: "invoice",
    vat_deposit_requires_cmr_in: "cmr",
    vat_deposit_requires_transporter_invoice: "vat-deposit-transporter-invoice",
    vat_deposit_requires_carzelle_affidavit: "vat-deposit-affidavit",
    vat_deposit_requires_transporter_affidavit: "vat-deposit-affidavit",
    vat_deposit_requires_payment_confirmation: "vat-deposit-payment-confirmation",
    vat_deposit_requires_entry_confirmation: "entry-confirmation",
    compound_confirmation_required: "compound-confirmation",
    compound_announcement_required: "compound-announcement",
    release_required: "release",
    cmr_required: "cmr",
    entry_confirmation_required: "entry-confirmation",
};

export function resolveIconName(name: string): IconName | null {
    if (name in ICON_REGISTRY) return name as IconName;
    return ICON_ALIASES[name] ?? null;
}

export function resolveIcon(name: string): LucideIcon | null {
    const key = resolveIconName(name);
    return key ? ICON_REGISTRY[key] : null;
}
